import React from 'react';
import { Link } from '@remix-run/react';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { H3 } from '~/components/ui/typography';
import type { BlogPost } from '~/types/blog';
import { cn } from '~/lib/utils';

interface BlogCardProps {
  post: BlogPost;
  variant?: 'grid' | 'horizontal';
}

// Helper function to format date as "Month DD, YYYY"
function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'Unknown date';

  try {
    let date: Date;

    // Check if it's an ISO 8601 format (includes T for time)
    if (dateString.includes('T')) {
      // ISO format with time - use standard parsing, it handles UTC correctly
      date = new Date(dateString);
    } else {
      // Simple YYYY-MM-DD format - parse manually to avoid timezone issues
      // Dates in format "YYYY-MM-DD" are parsed as UTC, which can shift the day
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts.map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Fallback to standard parsing if not in expected format
        date = new Date(dateString);
      }
    }

    // Validate the date is valid
    if (isNaN(date.getTime())) return 'Unknown date';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return 'Unknown date';
  }
}

export function BlogCard({ post, variant = 'grid' }: BlogCardProps) {
  // Handle both frontmatter structure and direct properties
  const title = post.frontmatter?.title || post.title;
  const description = post.frontmatter?.description || post.description || post.excerpt;
  const image = post.frontmatter?.image || post.image;
  const category = post.frontmatter?.category || post.category;
  const readingTime = post.frontmatter?.readingTime || post.readingTime;
  const date = post.frontmatter?.date || post.date;
  const author = post.frontmatter?.author || post.author;

  // Normalize author to always be an object or string
  const authorName = typeof author === 'string'
    ? author
    : author?.name || 'Unknown Author';

  // Grid variant (original design)
  if (variant === 'grid') {
    return (
      <Link to={`/blog/${post.slug}`} className="block group h-full">
        <Card className="h-full hover:shadow-lg hover:border-purple-600/50 transition-all duration-500 flex flex-col">
          {image && (
            <div className="relative aspect-[5/4] overflow-hidden rounded-t-lg">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-700"
                loading="lazy"
              />
            </div>
          )}
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary">{category}</Badge>
              <span className="text-sm text-muted-foreground">
                {readingTime} min read
              </span>
            </div>
            <H3 className="group-hover:text-primary transition-colors line-clamp-2">
              {title}
            </H3>
          </CardHeader>
          <CardContent className="flex flex-col flex-1">
            <p className="text-muted-foreground line-clamp-3 mb-4">
              {description}
            </p>
            <div className="text-sm text-muted-foreground mb-4 md:mb-0 mt-auto">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>{authorName}</span>
                  <span>◆</span>
                  <span>{formatDate(date)}</span>
                </div>
                <span className="text-primary hidden md:inline">
                  Read more →
                </span>
              </div>
            </div>
            {/* Mobile CTA Button */}
            <div className="md:hidden">
              <Button variant="default" fullWidth className="w-full">
                Read article →
              </Button>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Horizontal variant (full-width stacked)
  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <Card className="hover:shadow-lg hover:border-purple-600/50 transition-all duration-500 overflow-hidden">
        <div className={cn(
          "flex flex-col",
          image && "md:flex-row"
        )}>
          {/* Image */}
          {image && (
            <div className="relative w-full md:w-80 aspect-[5/4] overflow-hidden shrink-0">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform duration-700"
                loading="lazy"
              />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 flex flex-col">
            <CardHeader className="flex-1 space-y-4">
              <div className="flex items-center gap-2.5">
                <Badge variant="secondary" className="text-xs">{category}</Badge>
                <span className="text-sm text-muted-foreground">
                  {readingTime} min read
                </span>
              </div>
              <H3 className="text-2xl md:text-3xl font-bold group-hover:text-primary transition-colors duration-500 leading-tight">
                {title}
              </H3>
              <p className="text-base md:text-lg text-muted-foreground line-clamp-2 leading-relaxed">
                {description}
              </p>
            </CardHeader>
            <CardContent className="pt-0 pb-6 md:pb-6">
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span>{authorName}</span>
                  <span>◆</span>
                  <span>{formatDate(date)}</span>
                </div>
                <span className="text-primary hidden md:inline">
                  Read article →
                </span>
              </div>
            </CardContent>
          </div>

          {/* Mobile CTA Button */}
          <div className="md:hidden px-6 pb-6">
            <Button variant="default" fullWidth className="w-full">
              Read article →
            </Button>
          </div>
        </div>
      </Card>
    </Link>
  );
}